import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PulsingLogo from './PulsingLogo';
import config from '../../config/config';

// Fallback значения на случай, если config не загрузился
const FALLBACK_CONFIG = {
  app: {
    loginRoute: '/login'
  }
};

// Используем config или fallback
const currentConfig = config || FALLBACK_CONFIG;

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireAuth = true, 
  redirectTo = currentConfig.app.loginRoute 
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Показываем загрузку, пока проверяем аутентификацию
  if (isLoading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        gap: '40px'
      }}>
        {/* Логотип сверху */}
        <div style={{ marginTop: '60px', marginBottom: '30px' }}>
          <h1 className="protected-route-logo-title">
            Ryaskov.pro
          </h1>
        </div>
        
        {/* Концентрические дуги загрузки */}
        <div style={{ position: 'relative', margin: '40px 0', width: '100px', height: '100px' }}>
          {/* Внешняя дуга - толстая, 3/4 круга */}
          <svg width="100" height="100" style={{ position: 'absolute', top: '0', left: '0' }}>
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke="#ffffff"
              strokeWidth="6"
              strokeDasharray="188 63"
              strokeDashoffset="0"
              style={{ 
                animation: 'rotateArc1 2s linear infinite',
                transformOrigin: 'center',
                transformBox: 'fill-box'
              }}
            />
          </svg>
          
          {/* Средняя дуга - средняя толщина, 1/2 круга */}
          <svg width="80" height="80" style={{ position: 'absolute', top: '10px', left: '10px' }}>
            <circle
              cx="40"
              cy="40"
              r="30"
              fill="none"
              stroke="#ffffff"
              strokeWidth="4"
              strokeDasharray="94 94"
              strokeDashoffset="47"
              style={{ 
                animation: 'rotateArc2 1.5s linear infinite',
                transformOrigin: 'center',
                transformBox: 'fill-box'
              }}
            />
          </svg>
          
          {/* Внутренняя дуга - тонкая, 1/4 круга */}
          <svg width="60" height="60" style={{ position: 'absolute', top: '20px', left: '20px' }}>
            <circle
              cx="30"
              cy="30"
              r="20"
              fill="none"
              stroke="#ffffff"
              strokeWidth="2"
              strokeDasharray="47 141"
              strokeDashoffset="0"
              style={{ 
                animation: 'rotateArc3 1s linear infinite',
                transformOrigin: 'center',
                transformBox: 'fill-box'
              }}
            />
          </svg>
        </div>
        
        <style>{`
          .protected-route-logo-title {
            font-size: 3rem;
            font-weight: 700;
            color: #ffffff !important;
            margin: 0;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            text-shadow: 0 0 20px rgba(255, 255, 255, 0.5);
          }

          @keyframes rotateArc1 {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes rotateArc2 {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(-360deg); }
          }
          
          @keyframes rotateArc3 {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Если требуется аутентификация, но пользователь не авторизован
  if (requireAuth && !isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Если пользователь авторизован, но пытается зайти на страницу входа
  if (!requireAuth && isAuthenticated) {
    // Если есть сохраненный путь для редиректа, разрешаем доступ
    // (это нужно для страницы логина, чтобы она могла сделать navigate после логина)
    const from = location.state?.from?.pathname;
    if (from && from.startsWith('/invite/')) {
      // Разрешаем доступ к LoginScreen, если есть редирект на страницу приглашения
      return <>{children}</>;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
